/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConversationHistory, useFlow1HiringAssistant } from 'hooks';
import { SalaryDetails } from 'features';
import { appendIdToUrlAsQuery, Message, parseJobAssessmentData } from 'components';
import { parseCriteriaData } from 'helpers/parseCriteriaData';
import { appendAssistantIdToUrlAsQuery } from 'hooks/useResponseProcessing';
import { parseCVComparisonContent } from 'helpers/parseCvComparison';
import { handleStateTransition } from 'helpers/handleStateTransition';
import { isTalentAudit, isTalentAuditConversation, setAssistantFlow, TALENT_AUDIT_OPTION } from 'helpers/assistantFlow';
import { dedupeMessages } from 'helpers/messageDedupe';
import {
  AUDIT_CUSTOM_QUESTION_STATE,
  componentTypeForCustomQuestion,
  getCustomQuestionDraft,
  isCustomQuestionStage,
  resolveCustomQuestionDraft,
  withCustomQuestionEntry,
} from 'helpers/customQuestionDraft';
import {
  getControlInitialValues,
  getControlOptions,
  parseControlAnswer,
} from 'components/assistant-control';
import { getWelcomeOptionLabel } from 'components/chat/recruitment-cards/welcome-options';
import { AUDIT_TYPE_PROMPT, getAuditOptionLabel } from 'components/chat/recruitment-cards/audit-entry-point';
import { getHistoryDocumentHint } from 'helpers/documentMessage';
import {
  getAssistantComposerConfig,
  InputHints,
} from 'helpers/assistantInputHints';
import { normalizeWorkMode } from 'utils/workMode';

const CANDIDATES_TO_INVITE_KEY = 'cv_analysis_candidates_to_invite';

// The backend doesn't persist these framing messages in conversation history, so
// they're re-synthesised on replay. Kept in sync with useResponseProcessing, which
// emits the same copy live.
const CRITERIA_INTRO = () =>
  isTalentAudit()
    ? "Great! Based on your goals, I've prepared suggested evaluation dimensions for this review. \n Feel free to refine any section before proceeding."
    : "Perfect! I've reviewed the job description. \n Here's a draft of the key assessment criteria I plan to use to evaluate candidates. You're welcome to keep or remove any sections that aren't relevant to screen for at this stage.";

const QUESTIONS_INTRO = () =>
  isTalentAudit()
    ? "To better understand how employees apply their expertise in day-to-day work, I've curated a set of contextual technical questions focused on execution, problem-solving, and operational workflows."
    : "To validate the technical competence of your candidates, we've curated a list of targeted screening questions. These are designed to help candidates articulate how they've applied their skills in real-world scenarios.";

interface UseConversationProcessingProps {
  assessmentId: string | null;
  formData: Record<string, string | string[]>;
  setFormData: (data: Record<string, string | string[]>) => void;
  salaryDetails: SalaryDetails;
  setSalaryDetails: (details: SalaryDetails) => void;
  expectations: any;
  setMessages: (value: Message[] | ((prev: Message[]) => Message[])) => void;
  messages: Message[];
  setCurrentOptions: (options: any[] | null) => void;
  setShowOption: (show: boolean) => void;
  setShowInput: (show: boolean) => void;
  setInputHints?: (hints: InputHints | null) => void;
  setIsSubmittedQuestions: (value: boolean) => void;
  setConversationState: (state: string) => void;
  initialMessages: Message[];
  isInitialized: boolean;
  setIsInitialized: (initialized: boolean) => void;
  setAssessmentId: (id: string | null) => void;
  assessmentStatus: 'idle' | 'initial' | 'ready';
  setAssistantId: (id: string | null) => void;
  assistantId: string | null;
}

export const useConversationProcessing = ({
  assessmentId,
  formData,
  setFormData,
  salaryDetails,
  setSalaryDetails,
  expectations,
  setMessages,
  messages,
  setCurrentOptions,
  setShowOption,
  setShowInput,
  setInputHints,
  setIsSubmittedQuestions,
  setConversationState,
  initialMessages,
  isInitialized,
  setAssessmentId,
  setIsInitialized,
  assessmentStatus,
  setAssistantId,
  assistantId,
}: UseConversationProcessingProps) => {
  const [searchParams] = useSearchParams();
  const assessmentIdParam = searchParams.get('id');
  const assistantIdParam = searchParams.get('id2');
  const [historicalFormData, setHistoricalFormData] = useState<Record<string, string | string[]>>({});
  const [hasErrorHandled, setHasErrorHandled] = useState(false);
  const [lastProcessedConversationId, setLastProcessedConversationId] = useState<string | null>(null);
  const [candidatesToInvite, setCandidatesToInvite] = useState<{ full_name: string; email: string }[]>([]);
  const conversationId = assistantIdParam || assessmentIdParam || assistantId || assessmentId;
  const { response: flow1Response, loading: flow1Loading, error: flow1Error, sendRequest: flow1SendRequest } = useFlow1HiringAssistant(assistantId);

  const { data: conversationHistory, isLoading: conversationHistoryLoading, error } = useConversationHistory({
    id: conversationId,
    type: assistantIdParam ? 'assistant' : 'assessment',
    assessmentStatus,
    isInitialized,
  });

  useEffect(() => {
    if (error && !hasErrorHandled) {
      setHasErrorHandled(true);
      setShowInput(false);
      setShowOption(false);
      setMessages([
        {
          id: `${Date.now()}-error-history`,
          content: 'Sorry, there was a problem loading the conversation history. Please try again later.',
          sender: 'system',
          timestamp: new Date(),
        },
      ]);
    }
  }, [error, hasErrorHandled]);

  // URL params are always strings, but assessmentId/assistantId can be set to numbers
  // from API responses (data.assistant_id is numeric). A raw `!==` then compares '1564'
  // to 1564 → always true → these effects keep firing setIsInitialized(false), which
  // re-runs history reconstruction on stale cached data and clobbers live progress.
  // Compare as strings so a number/string mismatch never triggers a spurious reset.
  const assessmentIdChanged = !!assessmentIdParam && assessmentIdParam !== String(assessmentId ?? '');
  const assistantIdChanged = !!assistantIdParam && assistantIdParam !== String(assistantId ?? '');

  useEffect(() => {
    if (assessmentIdChanged) {
      appendIdToUrlAsQuery(assessmentIdParam!);
    }
  }, [assessmentIdChanged, assessmentIdParam]);

  useEffect(() => {
    if (assessmentIdChanged) {
      setAssessmentId(assessmentIdParam);
      setIsInitialized(false);
    }
  }, [assessmentIdChanged, assessmentIdParam, setAssessmentId, setIsInitialized]);

  useEffect(() => {
    if (assistantIdChanged) {
      appendAssistantIdToUrlAsQuery(assistantIdParam!);
    }
  }, [assistantIdChanged, assistantIdParam]);

  useEffect(() => {
    if (assistantIdChanged) {
      setAssistantId(assistantIdParam);
      setIsInitialized(false);
    }
  }, [assistantIdChanged, assistantIdParam, setAssistantId, setIsInitialized]);

  // Reset error handled state when conversation changes
  useEffect(() => {
    setHasErrorHandled(false);
  }, [conversationId]);

  const flowType = localStorage.getItem('hiringAssistantFlowType');

  const handleCandidatesToInviteChange = (candidates: { full_name: string; email: string }[]) => {
    setCandidatesToInvite(candidates);
    localStorage.setItem(CANDIDATES_TO_INVITE_KEY, JSON.stringify(candidates));

    const hasJobAssessmentMessage = messages.some((msg) => msg.componentType === 'jobAssessment');
    if (!hasJobAssessmentMessage) return;

    const updatedMessages = messages.map((msg) => {
      if (msg.componentType !== 'jobAssessment') return msg;
      return {
        ...msg,
        componentData: {
          ...msg.componentData,
          candidatesToInvite: candidates,
        },
      };
    });

    setMessages(updatedMessages);
  };

  useEffect(() => {
    // Error guard: if conversationHistory failed (500 or error), show error and stop loading
    if (error) {
      setShowInput(false);
      setShowOption(false);
      setMessages([
        {
          id: `${Date.now()}-error-history`,
          content: 'Sorry, there was a problem loading the conversation history. Please try again later.',
          sender: 'system',
          timestamp: new Date(),
        },
      ]);
      return;
    }
    // Only process when not initialized OR when conversation ID changed (prevents re-processing the same data)
    // Check if we've already processed this specific conversation
    const shouldProcess = !isInitialized || (conversationId && conversationId !== lastProcessedConversationId);
    if (!shouldProcess) {
      return;
    }
    // Clear any leftover historical state from a prior conversation so a new
    // assessment starts with empty fields instead of leaking previous values.
    setHistoricalFormData({});
    setFormData({});
    setSalaryDetails({ currency: 'USD', frequency: 'monthly', minSalary: '', maxSalary: '' });
    // Only set assessmentId from conversation history if it exists
    // Don't overwrite with null - the assessmentId might already be set from API response
    if (conversationHistory?.data?.assessment_id) {
      setAssessmentId(conversationHistory.data.assessment_id);
    }
    // A talent audit is keyed by assessment_id just like flow_2, so ?id= alone
    // can't identify it. The loaded transcript is authoritative — in BOTH
    // directions. Without the reset, switching from an audit to a hiring
    // conversation (which doesn't reload the page) leaves the audit flag set and
    // leaks audit copy — "Operational competence", "Invite employees" — into flow_2.
    if (conversationHistory?.data) {
      if (isTalentAuditConversation(conversationHistory.data)) {
        setAssistantFlow('flow_talent_audit');
      } else if (isTalentAudit()) {
        setAssistantFlow(assistantIdParam ? 'flow_1' : 'flow_2');
      }
    }
    if (conversationHistory?.data?.conversation_history && conversationId) {
      try {
        const historyEntries = conversationHistory.data.conversation_history;
        const currentState = conversationHistory.data?.current_state;
        const processedMessages: Message[] = [];
        let savedFormData: Record<string, string | string[]> = {};
        let savedSalaryDetails: SalaryDetails | null = null;
        let storedCandidatesToInvite: { full_name: string; email: string }[] = [];
        const earliestComponentTimestamps: { [key: string]: string } = {};

        const persistedCandidates = localStorage.getItem(CANDIDATES_TO_INVITE_KEY);
        if (persistedCandidates) {
          try {
            const parsedCandidates = JSON.parse(persistedCandidates);
            if (Array.isArray(parsedCandidates)) {
              storedCandidatesToInvite = parsedCandidates;
              setCandidatesToInvite(parsedCandidates);
            }
          } catch (e) {
            console.error('Failed to parse persisted invite candidates:', e);
          }
        }

        // Track core responsibilities from assistant's expectations as fallback
        let assistantCoreResponsibilities: string[] = [];
        // Track the full expectations core_responsibilities object for supplementing criteria data
        let assistantFullCoreResponsibilities: any = null;
        // Track the last options encountered so we can pass them to handleStateTransition
        let lastOptions: any[] | null = null;
        // Options carried on a `control` rather than as option pills — the audit's
        // experience-level answer is stored as the bare value and only the control
        // knows its label.
        let lastControlOptions: any[] | null = null;
        const isAuditConversation = isTalentAuditConversation(conversationHistory.data);
        let questionsWereSubmitted = false;
        // Composer/control shape of the most recent *unanswered* assistant turn.
        let lastHadControl = false;
        let lastWantsComposer = false;
        let lastInputHints: InputHints | null = null;
        // `lastOptions` is sticky by design (it never clears), so it can hold buttons
        // from several turns ago. This tracks only the latest assistant turn's own
        // options, which is what decides whether that turn wants a composer.
        let lastTurnOptions: any[] | null = null;

        historyEntries.forEach((entry: any, index: number) => {
          const isBackendSubmissionEvent =
            entry.role === 'user' &&
            (entry.state === 'create_jd' || entry.state === 'criteria') &&
            typeof entry.content === 'object' &&
            entry.content !== null;
          const isCustomQuestionPrompt =
            isCustomQuestionStage(entry.state) &&
            ((typeof entry.content === 'string' && entry.content.includes('What type of custom questions would you like to ask?')) ||
              (typeof entry.content === 'object' &&
                entry.content !== null &&
                typeof entry.content.message === 'string' &&
                entry.content.message.includes('What type of custom questions would you like to ask?')));

          // Extract core responsibilities from assistant's ui_hints expectations
          if (entry.role === 'assistant' && entry.state === 'create_jd' && typeof entry.content === 'object' && entry.content !== null) {
            const expectations = entry.content?.ui_hints?.expectations;
            if (expectations?.core_responsibilities) {
              const coreResp = expectations.core_responsibilities;
              assistantFullCoreResponsibilities = coreResp;
              assistantCoreResponsibilities = Object.keys(coreResp)
                .filter((key) => key.startsWith("responsibility") && coreResp[key] && coreResp[`check${key.replace('responsibility', '')}`])
                .map((key) => coreResp[key].toLowerCase().replace(/\s+/g, "_"));
            }
          }

          if (entry.role === 'user' && entry.state === 'create_jd' && typeof entry.content === 'object' && entry.content !== null) {
            const apiFormData = entry.content;
            // Extract work environment as an array (checkbox field expects array)
            const workEnv = apiFormData.experience_environment;
            const workEnvironmentArr = Array.isArray(workEnv) ? workEnv : (workEnv ? [workEnv] : []);

            // Extract core responsibilities from the user's submission, fall back to assistant's expectations
            const coreResp = apiFormData.core_responsibilities || {};
            const coreRespSelected = Object.keys(coreResp)
              .filter((key) => key.startsWith("responsibility") && coreResp[key] && coreResp[`check${key.replace('responsibility', '')}`])
              .map((key) => coreResp[key].toLowerCase().replace(/\s+/g, "_"));

            savedFormData = {
              typeOfEmployment: apiFormData.employment_type || '',
              experienceLevel: apiFormData.experience_level || '',
              workMode: normalizeWorkMode(apiFormData.work_mode),
              address: apiFormData.address || '',
              yearsOfExperience: apiFormData.years_of_experience || '',
              workEnvironment: workEnvironmentArr,
              coreResponsibilities: coreRespSelected.length > 0 ? coreRespSelected : assistantCoreResponsibilities,
            };
            savedSalaryDetails = {
              currency: apiFormData.salary_range_currency || 'USD',
              frequency: apiFormData.salary_range_term || 'monthly',
              minSalary: apiFormData.min_salary_range_amount?.toString() || '',
              maxSalary: apiFormData.max_salary_range_amount?.toString() || '',
            };
          }

          if (entry.role === 'user' && entry.state === 'technical_competence') {
            console.log('technical_competence entry raw content:', entry.content);
          }

          if (
            entry.role === 'user' &&
            isCustomQuestionStage(entry.state) &&
            typeof entry.content === 'object' &&
            entry.content !== null
          ) {
            const responseData = entry.content;
            const candidateQuestions: any[] = Array.isArray(responseData.questions)
              ? responseData.questions
              : ['question1', 'question2', 'question3']
                  .map((key) => responseData[key])
                  .filter((q) => q && typeof q === 'object');
            const detectedQuestionType = candidateQuestions.find((q) => q?.question_type)?.question_type;
            const isMultipleChoiceSubmission =
              responseData.action === 'create_multiple_choice_questions' ||
              detectedQuestionType === 'multiple_choice';
            const isOpenEndedSubmission =
              responseData.action === 'create_open_ended_questions' ||
              responseData.options?.[0]?.value === 'submit_open_ended' ||
              detectedQuestionType === 'open_ended';

            if (isOpenEndedSubmission || isMultipleChoiceSubmission) {
              console.log('technical_competence custom question entry content:', responseData);
              questionsWereSubmitted = true;
              setIsSubmittedQuestions(true);
              const componentType = isMultipleChoiceSubmission ? 'addMultipleQuestions' : 'addSingleQuestion';
              processedMessages.push({
                id: `${entry.timestamp}-${componentType}`,
                content: '',
                sender: 'system',
                timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                componentType,
                componentData: {
                  questions: responseData,
                },
                state: entry.state,
                isProcessed: false,
              });
            }
          }

          // The backend persists option clicks using the option's `value`, not its `text`
          // (e.g. content: "technical_competence" instead of "Everything is great, go ahead").
          // Recover the label from, in order: the options on the preceding assistant
          // turn, that turn's `control` options, then the static audit list for turns
          // the backend never stored at all.
          const isUserValue = entry.role === 'user' && typeof entry.content === 'string';
          const matchedControlText = !isUserValue || !Array.isArray(lastControlOptions)
            ? undefined
            : (() => {
                const values = parseControlAnswer(entry.content);
                if (values.length === 0) return undefined;

                const labels = values.map((value) =>
                  lastControlOptions!.find((option: any) => option?.value === value)?.label
                );
                if (!labels.some(Boolean)) return undefined;

                return labels.map((label, index) => label ?? values[index]).join(', ');
              })();
          const matchedOptionText = !isUserValue
            ? undefined
            : (Array.isArray(lastOptions)
                ? lastOptions.find((opt: any) => opt?.value === entry.content)?.text
                : undefined) ??
              matchedControlText ??
              (isAuditConversation ? getAuditOptionLabel(entry.content) : undefined);

          let content = '';
          // flow_1 blanks this because its ExperienceLevelForm shows the answer
          // inside the form. The audit's control doesn't, and live chat renders a
          // bubble, so keep the label there.
          if (entry.state === 'get_experience_level' && entry.role === 'user' && !isAuditConversation) {
            content = '';
          } else if (entry.content === 'upload') {
            content = "Yes, I'll upload it now";
          } else if (entry.content === 'done') {
            content = "No, I'm done";
          } else if (typeof entry.content === 'string' && getWelcomeOptionLabel(entry.content)) {
            // Welcome-card clicks are stored as the bare value ("evaluate",
            // "scratch", "talent_audit"). Those options are client-side, so they
            // never appear in `lastOptions` — label them from the shared list.
            content = getWelcomeOptionLabel(entry.content)!;
          } else if (matchedOptionText) {
            content = matchedOptionText;
          } else {
            if (typeof entry.content === 'string') {
              content = entry.content;
            } else if (typeof entry.content === 'object' && entry.content !== null) {
              if (entry.content.message && typeof entry.content.message === 'string') {
                content = isCustomQuestionPrompt ? '' : entry.content.message;
              } else if (entry.content.message && typeof entry.content.message === 'object') {
                // Object messages (questions, criteria data, etc.) are rendered by dedicated components -- skip text display
                content = '';
              } else {
                content = isBackendSubmissionEvent ? '' : 'Data submitted';
              }
            } else if (entry.content === null) {
              if (entry.state === 'get_role' && entry.role === 'user') {
                content = 'Role information provided';
              } else if (entry.state === 'get_jd_choice' && entry.role === 'user') {
                content = 'Job description option selected';
              } else if (isBackendSubmissionEvent) {
                content = '';
              } else {
                content = 'Action completed';
              }
            }
          }

          if (isBackendSubmissionEvent) {
            return;
          }

          const placeholderContent = content.trim();
          if (
            placeholderContent === 'Data submitted' ||
            placeholderContent === 'Preferences submitted' ||
            placeholderContent === 'Job preferences submitted successfully' ||
            placeholderContent === 'Action completed'
          ) {
            return;
          }

          const message: Message = {
            id: `${entry.timestamp || Date.now().toString()}-${index}`,
            content: content,
            sender: entry.role === 'user' ? 'user' : 'system',
            timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
            state: entry.state,
            documentHint: getHistoryDocumentHint(entry, historyEntries[index - 1]),
            isProcessed: false,
          };
          processedMessages.push(message);

          // The talent audit asks this same question with a server-driven `control`
          // (one combined list: "Mid-Level (4-7 years)"), rendered by assistantControl
          // above. flow_1 responses never carry `control`, so this cleanly skips the
          // legacy hardcoded level+years form instead of stacking both on replay.
          if (entry.state === 'get_experience_level' && !(entry.content as any)?.control) {
            const hasExistingOptionsForm = processedMessages.some(
              (msg) => msg.componentType === 'experienceLevelForm' && msg.state === entry.state
            );

            // Is the conversation still parked on this step (user hasn't submitted their
            // experience level yet)? If so, a reload must restore an INTERACTIVE form —
            // otherwise the flow dead-ends with no way to continue. The form's options are
            // hardcoded, so it renders without structured content; we only need the message.
            const isParkedHere = currentState === 'get_experience_level';

            // Create the form when the assistant prompt carried structured content (answered
            // history, as before) OR when we're parked here (in-progress reload — the fix).
            if (!hasExistingOptionsForm && (typeof entry.content === 'object' || isParkedHere)) {
              const optionsMessage: Message = {
                id: `${entry.timestamp}-experience-level-form`,
                content: '',
                sender: 'system',
                timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                componentType: 'experienceLevelForm',
                componentData: typeof entry.content === 'object' ? entry.content : {},
                state: entry.state,
                // isSubmitted = !isProcessed (see chatMessages). Parked → interactive;
                // already answered → read-only (matches prior behaviour).
                isProcessed: isParkedHere,
              };
              processedMessages.push(optionsMessage);
            }
          }

          if (
            entry.state === 'display_results' &&
            entry.role === 'assistant' &&
            entry.content !== 'manage_candidates'
          ) {
            const hasComparisonTable = processedMessages.some(
              msg => msg.componentType === 'cvComparison' && msg.state === entry.state
            );

            if (!hasComparisonTable) {
              // Prefer structured data if backend provides it
              const candidates = conversationHistory?.data?.context_data?.analysis_results;
              processedMessages.push({
                id: `${entry.timestamp}-cv-comparison`,
                content: '',
                sender: 'system',
                timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                componentType: 'cvComparison',
                componentData: {
                  candidates,
                  disclaimer:
                    "Our recommendations are based solely on the information provided in candidate CVs. Without additional context or behavioral data, these insights should be seen as an initial guide -- not a full evaluation of fit.\nFor best results, consider using our structured assessment tools to gain a deeper, evidence-based understanding of each candidate's capabilities.",
                  options: [
                    { text: 'Administer assessment', value: 'administer_assessment', color: 'primary' },
                    { text: 'Manage candidates', value: 'manage_candidates', color: 'secondary' },
                  ],
                  assistantId: assessmentId,
                },
                state: entry.state,
                isProcessed: false,
              });
            }
          }

          if (entry.role === 'assistant' && typeof entry.content === 'object' && entry.content !== null) {
            const responseData = entry.content;

            // Server-driven select form (talent audit). The recruiter's answer is
            // the plain user turn immediately following it. A control that is the
            // current unanswered turn must remain interactive after reload; older
            // answered controls replay checked and locked.
            if (responseData.control) {
              const componentKey = `assistantControl-${entry.state}-${entry.timestamp}`;
              if (!earliestComponentTimestamps[componentKey]) {
                earliestComponentTimestamps[componentKey] = entry.timestamp;
                const nextEntry = historyEntries[index + 1];
                const answer = nextEntry?.role === 'user' ? nextEntry : null;
                const answered = parseControlAnswer(answer?.content);
                const submittedValues = getControlInitialValues(
                  responseData.control,
                  answer?.content,
                );
                const isCurrentUnansweredControl =
                  currentState === entry.state &&
                  answered.length === 0 &&
                  index === historyEntries.length - 1;

                processedMessages.push({
                  id: `${entry.timestamp}-assistant-control`,
                  content: '',
                  sender: 'system',
                  timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                  componentType: 'assistantControl',
                  componentData: {
                    control: responseData.control,
                    submittedValues,
                    isHistorical: !isCurrentUnansweredControl,
                  },
                  state: entry.state,
                  isProcessed: true,
                });
              }
            }

            // The talent audit names the link `evaluation_link`; flow_2 uses `application_link`.
            if (responseData.ui_hints?.show_email_preview && (responseData.ui_hints?.application_link || responseData.ui_hints?.evaluation_link) && responseData.ui_hints?.decision_buckets) {

              const componentKey = `jobAssessment-${entry.state}`;
              if (!earliestComponentTimestamps[componentKey] || entry.timestamp < earliestComponentTimestamps[componentKey]) {
                earliestComponentTimestamps[componentKey] = entry.timestamp;
                const jobAssessmentData = parseJobAssessmentData(responseData.ui_hints);
                const jobAssessmentMessage: Message = {
                  id: `${entry.timestamp}-job-assessment`,
                  content: '',
                  sender: 'system',
                  timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                  componentType: 'jobAssessment',
                  componentData: {
                    ...jobAssessmentData,
                    candidatesToInvite: storedCandidatesToInvite,
                  },
                  state: entry.state,
                  isProcessed: false,
                };
                processedMessages.push(jobAssessmentMessage);
              }
            }

            if (entry.state === 'create_jd' && responseData.ui_hints?.show_expectations) {
              const hasExistingOptionsForm = processedMessages.some(
                (msg) => msg.componentType === 'optionsForm' && msg.state === entry.state
              );

              if (!hasExistingOptionsForm) {
                const optionsMessage: Message = {
                  id: `${entry.timestamp}-options-form`,
                  content: '',
                  sender: 'system',
                  timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                  componentType: 'optionsForm',
                  componentData: {
                    expectations: responseData.ui_hints.expectations,
                    state: entry.state,
                    historicalFormData: savedFormData,
                    historicalSalaryDetails: savedSalaryDetails,
                    isInitialRender: true,
                  },
                  state: entry.state,
                  isProcessed: false,
                };
                processedMessages.push(optionsMessage);
              }
            }

            // The timeline belongs to the turn that actually asks for it, not to
            // every turn filed under `done`. flow_2 parks that turn at `done`; the
            // talent audit uses state `timeline` with an explicit `show_timeline`
            // hint and then files its *final* turn — the one carrying the live
            // assessment card — under `done` too. Keying off the state alone
            // rebuilt a second timeline from that final turn, which is what left a
            // stray copy sitting below the assessment card on reload.
            const showsTimeline =
              responseData.ui_hints?.show_timeline === true ||
              ((entry.state === 'done' || entry.state === 'timeline') &&
                !responseData.ui_hints?.show_email_preview);

            if (showsTimeline) {
              // Deliberately not keyed by state: one timeline per conversation,
              // however many states the backend reports it under.
              const componentKey = 'timeline';
              if (!earliestComponentTimestamps[componentKey] || entry.timestamp < earliestComponentTimestamps[componentKey]) {
                earliestComponentTimestamps[componentKey] = entry.timestamp;
                const timelineMessage: Message = {
                  id: `${entry.timestamp}-timeline`,
                  content: '',
                  sender: 'system',
                  timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                  componentType: 'timeline',
                  componentData: {},
                  state: entry.state,
                  isProcessed: true,
                };
                processedMessages.push(timelineMessage);
              }
            }

            if (responseData.ui_hints?.show_jd_preview && responseData.ui_hints?.jd_preview) {
              const componentKey = `jobDescription-${entry.state}`;
              if (!earliestComponentTimestamps[componentKey] || entry.timestamp < earliestComponentTimestamps[componentKey]) {
                earliestComponentTimestamps[componentKey] = entry.timestamp;
                const jobDescMessage: Message = {
                  id: `${entry.timestamp}-job-desc`,
                  content: '',
                  sender: 'system',
                  timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                  componentType: 'jobDescription',
                  componentData: {
                    jdPreview: responseData.ui_hints.jd_preview,
                  },
                  state: entry.state,
                  isProcessed: false,
                };
                processedMessages.push(jobDescMessage);
              }
              setShowOption(true);
            }

            if (entry.state === 'criteria' && typeof responseData.message === 'object' && responseData.message !== null) {
              // Skip polling-status messages (poll/status set) — they only carry progress info,
              // not the full criteria payload. The actual criteria sometimes arrives in a later
              // technical_competence response and is rendered by that branch.
              const isPollingStatus = Boolean(responseData.message.status || responseData.message.poll);
              const criteriaData = isPollingStatus ? null : parseCriteriaData(responseData);
              if (criteriaData) {
                // Supplement core_responsibilities with the full list from expectations.
                // Old submissions only saved the selected (checked) items; use the expectations
                // object to restore the complete list with correct check status.
                if (assistantFullCoreResponsibilities) {
                  const expectCoreResp = assistantFullCoreResponsibilities;
                  const storedCoreResp = criteriaData.core_responsibilities || {};

                  // Build a set of checked titles from what was stored
                  const checkedTitles = new Set<string>();
                  let si = 1;
                  while (storedCoreResp[`responsibility${si}`]) {
                    if (storedCoreResp[`check${si}`]) {
                      checkedTitles.add((storedCoreResp[`responsibility${si}`] as string).toLowerCase());
                    }
                    si++;
                  }

                  // If stored data already has as many items as expectations, skip merging
                  const storedCount = si - 1;
                  let expectCount = 0;
                  while (expectCoreResp[`responsibility${expectCount + 1}`]) expectCount++;

                  if (storedCount < expectCount) {
                    const merged: any = {};
                    let idx = 1;
                    while (expectCoreResp[`responsibility${idx}`]) {
                      const title = expectCoreResp[`responsibility${idx}`];
                      merged[`responsibility${idx}`] = title;
                      merged[`description${idx}`] = expectCoreResp[`description${idx}`] || '';
                      merged[`check${idx}`] = checkedTitles.has((title as string).toLowerCase());
                      idx++;
                    }
                    criteriaData.core_responsibilities = merged;
                  }
                }
                const componentKey = `criteria-${entry.state}`;
                if (!earliestComponentTimestamps[componentKey] || entry.timestamp < earliestComponentTimestamps[componentKey]) {
                  earliestComponentTimestamps[componentKey] = entry.timestamp;
                  const preMessage: Message = {
                    id: `pre-criteria-${entry.timestamp || Date.now().toString()}-${index}`,
                    content: CRITERIA_INTRO(),
                    sender: 'system',
                    timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                    state: entry.state,
                    isProcessed: false,
                  };
                  const criteriaMessage: Message = {
                    id: `${entry.timestamp}-criteria`,
                    content: '',
                    sender: 'system',
                    timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                    componentType: 'jobCriteria',
                    componentData: {
                      criteriaData: criteriaData,
                    },
                    state: entry.state,
                    isProcessed: false,
                  };
                  processedMessages.push(preMessage, criteriaMessage);
                }
              }
              setCurrentOptions(responseData.options);
              setShowOption(true);
            }

            // The talent audit splits what flow_2 does under `technical_competence`
            // into two states: `review_criteria` carries the criteria payload and
            // `review_questions` the generated questions.
            if ((entry.state === 'technical_competence' || entry.state === 'review_criteria' || entry.state === 'review_questions') && entry.role === 'assistant' && typeof entry.content === 'object' && entry.content !== null) {
              const responseData = entry.content;

              // Check if this is a custom question form response (submit_open_ended / submit_multiple_choice)
              const customQuestionComponentType = (responseData?.options?.[0]?.value === 'submit_open_ended')
                ? 'addSingleQuestion'
                : (responseData?.options?.[0]?.value === 'submit_multiple_choice')
                  ? 'addMultipleQuestions'
                  : null;

              if (customQuestionComponentType) {
                setCurrentOptions(responseData.options);
                setShowOption(true);
                // The live handler renders the form itself for this response
                // (useResponseProcessing, `submit_open_ended` / `submit_multiple_choice`).
                // Restoring only the options here left a "Submit" affordance with
                // nothing above it to fill in, so mirror the live path.
                const componentKey = `${customQuestionComponentType}-${entry.state}`;
                if (!earliestComponentTimestamps[componentKey]) {
                  earliestComponentTimestamps[componentKey] = entry.timestamp;
                  processedMessages.push({
                    id: `${entry.timestamp}-${customQuestionComponentType}-backend`,
                    content: '',
                    sender: 'system',
                    timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                    componentType: customQuestionComponentType,
                    componentData: { questions: responseData.questions ?? null },
                    state: 'technical_competence',
                    isProcessed: false,
                  });
                }
              } else if (responseData.message && typeof responseData.message === 'object' && !responseData.message.status) {
                const messageData: any = responseData.message;
                const hasQuestionKeys = Object.keys(messageData).some(
                  (key) => /^question\d+$/.test(key) && messageData[key] && typeof messageData[key] === 'object'
                );
                const hasCriteriaKeys = Boolean(
                  messageData.personality ||
                  messageData.soft_skills ||
                  messageData.technical_skills ||
                  messageData.operational_competence ||
                  messageData.work_culture ||
                  messageData.basic_expectation
                );

                if (!hasQuestionKeys && hasCriteriaKeys) {
                  // Backend surfaces the full assessment criteria payload under a
                  // technical_competence response. Render it as the criteria card so the
                  // criteria stage isn't skipped in the rebuilt history.
                  const criteriaData = parseCriteriaData(responseData);
                  if (criteriaData) {
                    if (assistantFullCoreResponsibilities) {
                      const expectCoreResp = assistantFullCoreResponsibilities;
                      const storedCoreResp = criteriaData.core_responsibilities || {};
                      const checkedTitles = new Set<string>();
                      let si = 1;
                      while (storedCoreResp[`responsibility${si}`]) {
                        if (storedCoreResp[`check${si}`]) {
                          checkedTitles.add((storedCoreResp[`responsibility${si}`] as string).toLowerCase());
                        }
                        si++;
                      }
                      const storedCount = si - 1;
                      let expectCount = 0;
                      while (expectCoreResp[`responsibility${expectCount + 1}`]) expectCount++;
                      if (storedCount < expectCount) {
                        const merged: any = {};
                        let idx = 1;
                        while (expectCoreResp[`responsibility${idx}`]) {
                          const title = expectCoreResp[`responsibility${idx}`];
                          merged[`responsibility${idx}`] = title;
                          merged[`description${idx}`] = expectCoreResp[`description${idx}`] || '';
                          merged[`check${idx}`] = checkedTitles.has((title as string).toLowerCase());
                          idx++;
                        }
                        criteriaData.core_responsibilities = merged;
                      }
                    }
                    const componentKey = `criteria-criteria`;
                    if (!earliestComponentTimestamps[componentKey] || entry.timestamp < earliestComponentTimestamps[componentKey]) {
                      earliestComponentTimestamps[componentKey] = entry.timestamp;
                      const preMessage: Message = {
                        id: `pre-criteria-${entry.timestamp || Date.now().toString()}-${index}`,
                        content: CRITERIA_INTRO(),
                        sender: 'system',
                        timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                        state: 'criteria',
                        isProcessed: false,
                      };
                      const criteriaMessage: Message = {
                        id: `${entry.timestamp}-criteria`,
                        content: '',
                        sender: 'system',
                        timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                        componentType: 'jobCriteria',
                        componentData: {
                          criteriaData: criteriaData,
                        },
                        state: 'criteria',
                        isProcessed: false,
                      };
                      processedMessages.push(preMessage, criteriaMessage);
                    }
                  }
                } else if (hasQuestionKeys) {
                  // Standard questions component (the 5 generated questions)
                  const componentKey = `questions-${entry.state}`;
                  if (!earliestComponentTimestamps[componentKey] || entry.timestamp < earliestComponentTimestamps[componentKey]) {
                    earliestComponentTimestamps[componentKey] = entry.timestamp;
                    const preMessage: Message = {
                      id: `pre-questions-${entry.timestamp || Date.now().toString()}-${index}`,
                      content: QUESTIONS_INTRO(),
                      sender: 'system',
                      timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                      state: entry.state,
                      isProcessed: false,
                    };
                    const questionsMessage: Message = {
                      id: `${entry.timestamp || Date.now().toString()}-questions-${index}`,
                      content: '',
                      sender: 'system',
                      timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                      componentType: 'questions',
                      componentData: {
                        questions: responseData.message,
                      },
                      state: entry.state,
                      isProcessed: false,
                    };
                    processedMessages.push(preMessage, questionsMessage);
                  }
                }
              }
            }

            if (responseData.options && responseData.options.length > 0) {
              lastOptions = responseData.options;
              setCurrentOptions(responseData.options);
            }

            if (responseData.control) {
              lastControlOptions = getControlOptions(responseData.control);
            }
          }

          // "What type of custom questions would you like to ask?" is asked from
          // `technical_competence` but also from `done`, and only the former is
          // covered by the options capture above. Without this, reloading on that
          // question restored no buttons at all and the recruiter had no way back
          // into the stage.
          if (
            isCustomQuestionPrompt &&
            entry.role === 'assistant' &&
            typeof entry.content === 'object' &&
            entry.content !== null &&
            Array.isArray(entry.content.options) &&
            entry.content.options.length > 0
          ) {
            lastOptions = entry.content.options;
            setCurrentOptions(entry.content.options);
          }

          // Track the composer/control shape of the most recent assistant turn so
          // the state transition below can restore the right affordance on reload.
          if (entry.role === 'assistant' && typeof entry.content === 'object' && entry.content !== null) {
            lastHadControl = !!entry.content.control;
            const composerConfig = getAssistantComposerConfig(entry.content.ui_hints);
            lastWantsComposer = composerConfig.wantsComposer;
            lastTurnOptions = entry.content.options ?? null;
            lastInputHints = composerConfig.inputHints;
          } else if (entry.role === 'user') {
            // The recruiter has already answered, so the prompt's affordance is spent.
            lastHadControl = false;
            lastWantsComposer = false;
            lastInputHints = null;
          }
        });

        // Defensive dedupe: every interactive/UI component must appear at most once per state,
        // regardless of how many times the backend repeats it in the history. This guards the
        // render layer from any earliestComponentTimestamps gaps (clock skew, repeated entries).
        const filteredMessages = processedMessages.filter((message, index, self) => {
          if (!message.componentType) return true;

          // A state can legitimately ask more than one server-driven control.
          // Their timestamp-derived ids already remove exact duplicates; grouping
          // only by component type + state would discard a later valid form.
          if (message.componentType === 'assistantControl') {
            return self.findIndex((candidate) => candidate.id === message.id) === index;
          }

          const matchingMessages = self.filter(
            (candidate) => candidate.componentType === message.componentType && candidate.state === message.state
          );

          if (matchingMessages.length <= 1) return true;

          const getQuestionPayload = (candidate: Message) => candidate.componentData?.questions;
          const hasHydratedQuestions = (candidate: Message) => {
            const questions = getQuestionPayload(candidate);
            if (!questions || typeof questions !== 'object') return false;

            const questionValues = Array.isArray(questions)
              ? questions
              : Object.values(questions);

            return questionValues.some((question: any) => {
              if (!question || typeof question !== 'object') return false;
              const questionContent = question.question_content ?? question.question ?? '';
              return typeof questionContent === 'string' && questionContent.trim().length > 0;
            });
          };

          if (message.componentType === 'addSingleQuestion' || message.componentType === 'addMultipleQuestions') {
            const hydratedIndex = self.findIndex(
              (candidate) =>
                candidate.componentType === message.componentType &&
                candidate.state === message.state &&
                hasHydratedQuestions(candidate)
            );

            if (hydratedIndex !== -1) {
              return index === hydratedIndex;
            }
          }

          const firstIndex = self.findIndex(
            (candidate) => candidate.componentType === message.componentType && candidate.state === message.state
          );
          return index === firstIndex;
        });

        filteredMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        // ...and a second pass for the stage components that must be unique across
        // the whole conversation, not merely per state. Run after the sort so the
        // copy that survives is the chronologically first one.
        const dedupedMessages = dedupeMessages(filteredMessages);

        // The backend persists nothing for the talent audit's entry point — stored
        // history begins at the answer to the audit-type question. Reconstruct that
        // opening exchange so a reload matches what the recruiter saw live: their
        // card click, then the assistant's "what would you like to evaluate?".
        // Reaching this flow at all means the card was clicked.
        // (flow_2's "scratch" has the same gap; left as-is on purpose so this
        // doesn't silently change that flow's appearance.)
        if (isAuditConversation) {
          const auditLabel = getWelcomeOptionLabel(TALENT_AUDIT_OPTION);
          const alreadyPresent = dedupedMessages.some(
            (msg) => msg.sender === 'user' && msg.content === auditLabel
          );
          if (auditLabel && !alreadyPresent) {
            const anchor = dedupedMessages[0]?.timestamp?.getTime() ?? Date.now();
            dedupedMessages.unshift(
              {
                id: 'talent-audit-entry-point',
                content: auditLabel,
                sender: 'user',
                timestamp: new Date(anchor - 2),
                isProcessed: true,
              },
              {
                id: 'talent-audit-type-prompt',
                content: AUDIT_TYPE_PROMPT,
                sender: 'system',
                timestamp: new Date(anchor - 1),
                state: 'get_audit_type',
                isProcessed: true,
              }
            );
          }
        }

        // Track whether any interactive UI (options or input) will actually be shown.
        // Every setter that makes UI visible goes through these wrappers so the
        // safety net below can detect a fully stuck/unusable chat state.
        let uiWillShow = false;
        const guardedSetShowOption = (val: boolean) => { if (val) uiWillShow = true; setShowOption(val); };
        const guardedSetShowInput  = (val: boolean) => { if (val) uiWillShow = true; setShowInput(val); };

        // Apply the current state and its UI transitions so options/input show correctly
        if (currentState) {
          setConversationState(currentState);
          handleStateTransition({
            state: currentState,
            // When the last assistant turn asked for a control or typed answer, that
            // turn is authoritative — judge it on its own options, not on `lastOptions`,
            // which may still hold buttons from an earlier step and would otherwise
            // resurrect them instead of the composer.
            // The audit's assistant never offers the custom-questions stage, so its
            // entry point is added back on reload the same way the live path adds it.
            options: withCustomQuestionEntry(
              (lastHadControl || lastWantsComposer) ? lastTurnOptions : lastOptions,
              currentState,
              isAuditConversation
            ) ?? null,
            setShowOption: guardedSetShowOption,
            setShowInput: guardedSetShowInput,
            setCurrentOptions,
            hasControl: lastHadControl,
            wantsComposer: lastWantsComposer,
          });
          setInputHints?.(lastInputHints);
        }

        // Rebuild the custom-questions stage. Both halves of it are client-side —
        // choosing "Open ended" / "Multiple choice" is never sent to the assistant,
        // and skipping the form posts nothing — so history alone can't tell whether
        // the recruiter is mid-form, done, or was never here. The locally-stored
        // draft supplies exactly that, and without it a reload landed on a
        // transcript with no form, no Proceed button and no options.
        const customQuestionDraft = getCustomQuestionDraft(assessmentIdParam);
        const onCustomQuestionStage = isCustomQuestionStage(currentState);
        // An empty form already in the rebuild (backend-driven) is the same stage —
        // don't stack a second one on top of it.
        const alreadyHasOpenQuestionForm = dedupedMessages.some(
          (msg) =>
            (msg.componentType === 'addSingleQuestion' || msg.componentType === 'addMultipleQuestions') &&
            !msg.componentData?.questions
        );
        const restoredParkedQuestionForm =
          customQuestionDraft?.status === 'open' &&
          onCustomQuestionStage &&
          !alreadyHasOpenQuestionForm;

        if (restoredParkedQuestionForm && customQuestionDraft) {
          const componentType = componentTypeForCustomQuestion(customQuestionDraft.type);
          const anchor = dedupedMessages[dedupedMessages.length - 1]?.timestamp?.getTime() ?? Date.now();
          dedupedMessages.push({
            id: `restored-${componentType}`,
            content: '',
            sender: 'system',
            timestamp: new Date(anchor + 1),
            componentType,
            componentData: { questions: null },
            // Filed under the stage's canonical state, whichever state the stage
            // was reached from.
            state: 'technical_competence',
            isProcessed: true,
          });
          // The form owns its own submit/skip buttons, so nothing else belongs
          // under it — and it must render interactive, not as a read-only replay.
          setIsSubmittedQuestions(false);
          setCurrentOptions(null);
          setShowOption(false);
          setShowInput(false);
        }

        // The stage is finished with but the recruiter hasn't moved on: show the way
        // forward again. `questionsWereSubmitted` covers questions the server stored;
        // a resolved draft additionally covers a skipped form, which it never sees.
        // flow_2 always comes back to the same Proceed CTA; the audit comes back to
        // whatever its assistant offered at review_questions, which the draft kept —
        // and with nothing kept there is nothing to restore, so the ordinary history
        // restore above is left to stand.
        // `done` is deliberately not a resume stage, as before: the timeline owns the
        // screen there and option pills are suppressed, so forcing a CTA in would show
        // nothing while still convincing the stuck-state safety net that the UI is fine.
        const customQuestionsDone =
          questionsWereSubmitted || customQuestionDraft?.status === 'resolved';
        const stageResumeOptions =
          currentState === 'technical_competence'
            ? [{ text: 'Proceed', value: 'continue_technical_competence' }]
            : currentState === AUDIT_CUSTOM_QUESTION_STATE
              ? customQuestionDraft?.resumeOptions ?? null
              : null;
        if (stageResumeOptions?.length && customQuestionsDone && !restoredParkedQuestionForm) {
          setCurrentOptions(stageResumeOptions);
          guardedSetShowOption(true);
          guardedSetShowInput(false);
        }

        // Fallback for flow 2 reload: if criteria is shown but no options were restored from history,
        // show input so the recruiter can continue (matches the live polling fallback behaviour).
        const hasCriteriaComponent = dedupedMessages.some((msg) => msg.componentType === 'jobCriteria');
        if (
          hasCriteriaComponent &&
          !questionsWereSubmitted &&
          !lastOptions &&
          (currentState === 'technical_competence' || currentState === 'criteria')
        ) {
          guardedSetShowOption(false);
          guardedSetShowInput(true);
        }

        // Flow 1 reload: criteria generated in the background while current_state remained
        // "display_results". handleStateTransition hides everything for that state, so we
        // explicitly restore the criteria options so the recruiter can proceed.
        const snapshotOptions: any[] | null = lastOptions as any;
        const criteriaReadyOptions: any[] | null =
          snapshotOptions !== null &&
          snapshotOptions.some((opt: any) => opt?.value === 'technical_competence')
            ? snapshotOptions
            : null;
        if (currentState === 'display_results' && hasCriteriaComponent && criteriaReadyOptions) {
          setCurrentOptions(criteriaReadyOptions);
          guardedSetShowOption(true);
          guardedSetShowInput(false);
        }

        // Safety net: if no interactive UI will be visible for a chat that has content,
        // restore the last known options or fall back to text input so the user is never stuck.
        // States whose components include their own submit buttons are intentionally excluded.
        // get_experience_level renders the experience-level/years form, which has its own
        // Continue button — without it here the safety net forces the text input back under
        // the form even though handleStateTransition already hid it.
        const selfContainedStates = new Set(['create_jd', 'show_job_criteria', 'submit_jd', 'get_experience_level']);
        if (
          !uiWillShow &&
          !restoredParkedQuestionForm &&
          !lastHadControl &&
          dedupedMessages.length > 0 &&
          !selfContainedStates.has(currentState)
        ) {
          if (snapshotOptions && snapshotOptions.length > 0) {
            setCurrentOptions(snapshotOptions);
            setShowOption(true);
          } else if (currentState !== 'display_results') {
            // display_results without options: CV comparison table has embedded action buttons
            setShowInput(true);
          }
        }

        // The transcript is more reliable than the stale local-storage value we
        // read at render time. When an audit is opened directly from a copied URL,
        // local storage can still say flow_2 until the beginning of this effect
        // corrects it; do not prepend the hiring-flow conversation in that window.
        if (flowType === 'flow_2' && !isAuditConversation) {

          const additionalInitialMessages: Message[] = [
            {
              id: 'hire-msg',
              content: `I want to hire for a role, I want you to source, screen and coordinate the process`,
              sender: 'user',
              timestamp: new Date(),
              isProcessed: false,
            },
            {
              id: 'next-msg',
              content: `Great! Let's get started.`,
              sender: 'system',
              timestamp: new Date(),
              isProcessed: false,
            },
            {
              id: 'hiring-msg',
              content: `What role are you hiring for?`,
              sender: 'system',
              timestamp: new Date(),
              isProcessed: false,
            },
          ];

          const combinedMessages = [...initialMessages, ...additionalInitialMessages, ...dedupedMessages];
          setMessages(dedupeMessages(combinedMessages));
        } else {
          setMessages(dedupeMessages([...initialMessages, ...dedupedMessages]));
        }

        if (Object.keys(savedFormData).length > 0) {
          setFormData(savedFormData);
          setHistoricalFormData(savedFormData);
        }
        if (savedSalaryDetails) {
          setSalaryDetails(savedSalaryDetails);
        }

        // Mark as initialized after successful processing and track the processed conversation ID
        setIsInitialized(true);
        setLastProcessedConversationId(conversationId);
      } catch (error) {
        console.error('Error processing conversation history:', error);
      }
    }
  }, [conversationHistory, conversationHistoryLoading, conversationId, isInitialized, lastProcessedConversationId, assistantIdParam, assessmentIdParam]);


  const handleFormChange = (questionId: string, value: string | string[]) => {
    setFormData({
      ...formData,
      [questionId]: value,
    });
  };

  const handleSalaryChange = (details: SalaryDetails) => {
    setSalaryDetails(details);
  };

  const submitQuestion = ({ skipped = false }: { skipped?: boolean } = {}) => {
    // The form is done with. Marked resolved rather than dropped: a skip posts
    // nothing to the server, so this record is the only thing that lets a reload
    // restore the Proceed CTA instead of dead-ending the stage.
    resolveCustomQuestionDraft();
    // flow_2 always comes back to the same Proceed CTA. The audit's way forward is
    // whatever its assistant offered at review_questions — it differs per audit
    // type — so the draft hands back the options the stage was opened from, and
    // the copy stops naming a button that flow doesn't have.
    const resumeOptions = getCustomQuestionDraft(assessmentId ?? assessmentIdParam)?.resumeOptions;
    const outcome = skipped ? 'Question skipped.' : 'Question submitted successfully.';
    const submissionMessage: Message = {
      id: `${Date.now()}-system-${Math.random().toString(36).substr(2, 9)}`,
      content: resumeOptions?.length ? outcome : `${outcome} Proceed to continue.`,
      sender: 'system',
      timestamp: new Date(),
      state: 'technical_competence',
    };
    setMessages((prev) => [...prev, submissionMessage]);
    setCurrentOptions(
      resumeOptions?.length
        ? resumeOptions
        : [{ text: 'Proceed', value: 'continue_technical_competence' }]
    );
    setShowOption(true);
    setShowInput(false);
    setIsSubmittedQuestions(true);
  };

  return {
    conversationHistoryLoading,
    handleFormChange,
    handleSalaryChange,
    submitQuestion,
    historicalFormData,
    candidatesToInvite,
    handleCandidatesToInviteChange,
  };
};
